import { useTranslation } from 'react-i18next';
import { ArrowRightIcon } from '@bi-digital/icons';
import { useId } from 'react';
import { Chip } from '@bi-digital/react';
import { ResponsiveImage } from '@bino/utils/components/responsive-image';
import type { Opportunity } from '../../types';
import styles from './opportunity-promo-card.module.css';

const FALLBACK_IMAGE_URL = '/public/images/fallback_image.webp';

const MAX_CHIPS = 4;

export type OpportunityPromoCardProps = {
    opportunity: Opportunity;
    onSelect: () => void;
};

export const OpportunityPromoCard = ({ opportunity, onSelect }: OpportunityPromoCardProps) => {
    const { t } = useTranslation();
    const cardId = useId();
    const chips = (opportunity.electives ?? []).slice(0, MAX_CHIPS).map((elective) => elective.title);

    const Component = opportunity.href ? 'a' : 'button';

    return (
        <li className='promo-card-wrapper'>
            <div className='promo-card' data-clickdelegatefor={cardId}>
                <div className='border-hover' aria-hidden='true' />
                <div className='border-hover-corner' aria-hidden='true' />
                <div className='promo-card-media'>
                    <ResponsiveImage
                        image={opportunity.image}
                        alt={opportunity.imageAltText ?? ''}
                        fallbackImageUrl={FALLBACK_IMAGE_URL}
                    />
                </div>
                <div className='promo-card-container'>
                    <p className='title'>
                        <Component
                            href={opportunity.href}
                            onClick={opportunity.href ? undefined : onSelect}
                            type={opportunity.href ? undefined : 'button'}
                            id={cardId}
                        >
                            {opportunity.title}
                        </Component>
                    </p>
                    {opportunity.description && <p className='description'>{opportunity.description}</p>}
                    {chips.length > 0 && (
                        <div className={styles.chipRow}>
                            <p className={styles.chipHeading}>
                                {t('courseDescription.opportunities.electives.chipsHeader')}
                            </p>
                            <div className={styles.chips}>
                                {chips.map((chip, index) => (
                                    <Chip className={styles.chip} key={index}>
                                        {chip}
                                    </Chip>
                                ))}
                            </div>
                        </div>
                    )}
                    <ArrowRightIcon size='large' aria-hidden='true' />
                </div>
            </div>
        </li>
    );
};
